/**
 * Optional Phase 6: hybrid post-quantum recipient mode.
 * ML-KEM-768 + X25519 (noble ml_kem768_x25519) → AES-256-GCM payload.
 * Separate from password-based ZeroCrypt containers. Not "quantum-proof".
 */
import { ml_kem768_x25519 } from '@noble/post-quantum/hybrid.js';
import {
  asBufferSource,
  fromBase64Url,
  toBase64Url,
  utf8Encode,
  utf8Decode,
} from '../utils/bytes.ts';
import { wipeBytes } from '../utils/wipe.ts';

export const PQ_TEXT_PREFIX = 'ZC-PQ1:';
export const PQ_FILE_EXTENSION = '.zcpq' as const;
export const PQ_FILE_MIME = 'application/vnd.zerocrypt-pq' as const;

const PQ_MAGIC = new TextEncoder().encode('ZCPQ');

export type RecipientKeyPair = {
  publicKey: string;
  secretKey: string;
};

export function generateRecipientKeys(): RecipientKeyPair {
  const seedLen = ml_kem768_x25519.lengths.seed ?? 64;
  const seed = crypto.getRandomValues(new Uint8Array(seedLen));
  const keys = ml_kem768_x25519.keygen(seed);
  wipeBytes(seed);
  return {
    publicKey: `ZCPK1:${toBase64Url(keys.publicKey)}`,
    secretKey: `ZCSK1:${toBase64Url(keys.secretKey)}`,
  };
}

function parsePublicKey(encoded: string): Uint8Array {
  const t = encoded.trim();
  if (!t.startsWith('ZCPK1:')) throw new Error('Invalid recipient public key');
  return fromBase64Url(t.slice(6));
}

function parseSecretKey(encoded: string): Uint8Array {
  const t = encoded.trim();
  if (!t.startsWith('ZCSK1:')) throw new Error('Invalid recipient secret key');
  return fromBase64Url(t.slice(6));
}

async function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', asBufferSource(raw), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/** Binary container: magic | kem_ct_len_u32be | kem_ct | nonce12 | aes_sealed */
export async function encryptBytesToRecipient(
  recipientPublicKey: string,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  const pk = parsePublicKey(recipientPublicKey);
  const { cipherText: kemCt, sharedSecret } = ml_kem768_x25519.encapsulate(pk);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  try {
    const key = await importAesKey(sharedSecret);
    const sealed = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: asBufferSource(nonce), tagLength: 128 },
        key,
        asBufferSource(plaintext),
      ),
    );
    const ctLen = new Uint8Array(4);
    new DataView(ctLen.buffer).setUint32(0, kemCt.length, false);
    const body = new Uint8Array(
      PQ_MAGIC.length + 4 + kemCt.length + nonce.length + sealed.length,
    );
    let o = 0;
    body.set(PQ_MAGIC, o);
    o += PQ_MAGIC.length;
    body.set(ctLen, o);
    o += 4;
    body.set(kemCt, o);
    o += kemCt.length;
    body.set(nonce, o);
    o += nonce.length;
    body.set(sealed, o);
    return body;
  } finally {
    wipeBytes(sharedSecret);
  }
}

export async function decryptBytesFromRecipient(
  recipientSecretKey: string,
  body: Uint8Array,
): Promise<Uint8Array> {
  if (body.length < PQ_MAGIC.length + 4 + 12 + 16) {
    throw new Error('Truncated PQ payload');
  }
  for (let i = 0; i < PQ_MAGIC.length; i++) {
    if (body[i] !== PQ_MAGIC[i]) throw new Error('Bad PQ magic');
  }
  const kemCtLen = new DataView(body.buffer, body.byteOffset, body.byteLength).getUint32(
    PQ_MAGIC.length,
    false,
  );
  const kemStart = PQ_MAGIC.length + 4;
  const kemEnd = kemStart + kemCtLen;
  const nonceStart = kemEnd;
  const nonceEnd = nonceStart + 12;
  if (nonceEnd > body.length) throw new Error('Truncated PQ payload');
  const kemCt = body.subarray(kemStart, kemEnd);
  const nonce = body.subarray(nonceStart, nonceEnd);
  const sealed = body.subarray(nonceEnd);

  const sk = parseSecretKey(recipientSecretKey);
  let sharedSecret: Uint8Array | null = null;
  try {
    sharedSecret = ml_kem768_x25519.decapsulate(kemCt, sk);
    const key = await importAesKey(sharedSecret);
    try {
      return new Uint8Array(
        await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: asBufferSource(nonce), tagLength: 128 },
          key,
          asBufferSource(sealed),
        ),
      );
    } catch {
      throw new Error('PQ decryption failed (wrong key or corrupted payload)');
    }
  } finally {
    wipeBytes(sharedSecret);
    wipeBytes(sk);
  }
}

/**
 * Encrypt to a recipient public key.
 * Text wire format: ZC-PQ1: base64url(binary container)
 */
export async function encryptToRecipient(
  recipientPublicKey: string,
  plaintext: string | Uint8Array,
): Promise<string> {
  const pt = typeof plaintext === 'string' ? utf8Encode(plaintext) : plaintext;
  const body = await encryptBytesToRecipient(recipientPublicKey, pt);
  return `${PQ_TEXT_PREFIX}${toBase64Url(body)}`;
}

export async function decryptFromRecipient(
  recipientSecretKey: string,
  payload: string | Uint8Array,
): Promise<Uint8Array> {
  if (typeof payload !== 'string') {
    return decryptBytesFromRecipient(recipientSecretKey, payload);
  }
  const trimmed = payload.trim();
  if (!trimmed.startsWith(PQ_TEXT_PREFIX)) {
    throw new Error('Not a ZeroCrypt PQ payload');
  }
  const body = fromBase64Url(trimmed.slice(PQ_TEXT_PREFIX.length));
  return decryptBytesFromRecipient(recipientSecretKey, body);
}

export async function decryptFromRecipientText(
  recipientSecretKey: string,
  payload: string,
): Promise<string> {
  const bytes = await decryptFromRecipient(recipientSecretKey, payload);
  try {
    return utf8Decode(bytes);
  } finally {
    wipeBytes(bytes);
  }
}

export function defaultPqEncryptedFilename(originalName?: string): string {
  if (!originalName) return `encrypted${PQ_FILE_EXTENSION}`;
  const base = originalName.replace(/\.[^/.]+$/, '') || 'file';
  return `${base}${PQ_FILE_EXTENSION}`;
}
